// #3637: the one guarded path to Web Storage for render and mount code.
//
// A browser that blocks storage (iOS Safari "Block All Cookies", some in-app
// webviews, sandboxed iframes) throws SecurityError on ANY access, including
// merely reading `window.localStorage` / `window.sessionStorage`. Unguarded, a
// throw in a render path or a mount effect takes the whole app down to the
// error boundary before the auth stop can render (mega116 TP-3578-1). A full
// store throws QuotaExceededError on setItem.
//
// Every method here swallows ANY error: getItem returns null, setItem and
// removeItem return false. Callers therefore see "nothing stored" / "not
// stored" and must fail toward a working page.
//
// NOT for the session token. services/authFetch.js keeps its own chokepoint
// (getStoredAuthToken / storeAuthToken / clearStoredAuthToken): storeAuthToken
// must THROW STORAGE_UNAVAILABLE so login() never reports a session that was
// never stored. A `false` from setItem here would silently break that.
//
// Raw `localStorage` / `sessionStorage` are banned by lint outside this module
// (eslint.config.js, no-restricted-globals + no-restricted-properties).

function resolveStore(kind) {
  try {
    if (typeof window === 'undefined') return null;
    const store = kind === 'session' ? window.sessionStorage : window.localStorage;
    return store || null;
  } catch {
    return null;
  }
}

function createSafeStore(kind) {
  return Object.freeze({
    getItem(key) {
      try {
        const store = resolveStore(kind);
        return store ? store.getItem(key) : null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        const store = resolveStore(kind);
        if (!store) return false;
        store.setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    removeItem(key) {
      try {
        const store = resolveStore(kind);
        if (!store) return false;
        store.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  });
}

export const safeLocalStorage = createSafeStore('local');
export const safeSessionStorage = createSafeStore('session');
